import { getIconSpriteHref, type IconSpriteKey } from "@theme/.generated/icon-sprite-map";
import { getSvgSpriteHref, type SvgSpriteKey } from "@theme/.generated/svg-sprite-map";
import { getImage } from "gatsby-plugin-image";
import invariant from "invariant";

import type { StrapiBackgroundImagesInterface } from "@theme/types";
import type { IGatsbyImageData } from "gatsby-plugin-image";

export interface MockImageProps {
  width: number;
  height: number;
  color?: string;
  text?: string;
}

/**
 * Get Mock Fluid Image
 *
 * Since docz doesn't like graphql image queries
 * This function will return dummy image object
 * to allow image to load in docz. its using via.placeholder.com
 * to keep it generic as possible.
 *
 * @param {number} width - width of image
 * @param {number} height - height of image
 * @param {string} text - if you want custom text
 * @return {MockFluidImage} mock image object
 */
export const getMockImage = ({
  width,
  height,
  color = "CCCCCC",
  text = "Mock Image",
}: MockImageProps): IGatsbyImageData => {
  const convertedText = text.replace(/ /g, "+");
  const mockImageSrc = `https://placehold.co/${width}x${height}/${color}/jpg?text=${convertedText}`;

  const mockImage = getImage({
    layout: "fullWidth",
    width: width,
    height: height,
    images: {
      fallback: {
        src: mockImageSrc,
        srcSet: `${mockImageSrc} 200w, ${mockImageSrc} 400w, ${mockImageSrc} 800w, ${mockImageSrc} 1200w, ${mockImageSrc} 1600w, ${mockImageSrc} 2560w`,
        sizes: "100vw",
      },
      sources: [
        {
          srcSet: `${mockImageSrc} 200w, ${mockImageSrc} 400w, ${mockImageSrc} 800w, ${mockImageSrc} 1200w, ${mockImageSrc} 1600w, ${mockImageSrc} 2560w`,
          type: "image/png",
          sizes: "100vw",
        },
      ],
    },
  } as IGatsbyImageData);

  invariant(mockImage, "Mock Image must exist");

  return mockImage;
};

// Mock background images
export const getMockStrapiBackgroundImages = (options?: {
  heights?: {
    image480?: number;
    image768?: number;
    image1024?: number;
    image1366?: number;
    image2560?: number;
  };
}): StrapiBackgroundImagesInterface => {
  const heights = {
    image480: 574,
    image768: 669,
    image1024: 800,
    image1366: 800,
    image2560: 800,
    ...options?.heights,
  };

  const createImage = (width: number, height: number, text: string, color: string) => ({
    alternativeText: `Background ${text}`,
    cloudinaryMedia: {
      gatsbyImageData: getMockImage({ width, height, text, color }),
      width,
      height,
    },
  });

  return {
    image480: createImage(480, heights.image480, "480px Bg", "1A1A1A"),
    image768: createImage(768, heights.image768, "768px Bg", "2A2A2A"),
    image1024: createImage(1024, heights.image1024, "1024px Bg", "3A3A3A"),
    image1366: createImage(1366, heights.image1366, "1366px Bg", "4A4A4A"),
    image2560: createImage(2560, heights.image2560, "2560px Bg", "5A5A5A"),
  };
};

/**
 * Optimize Cloudinary Secure URL
 *
 * This function takes a Cloudinary secure URL and adds
 * the f_auto and q_auto parameters to it
 *
 * graphql does not include this as secure_url is the source and not the optimized version
 *
 * regex below is to check if the url already has f_auto or q_auto after /upload/
 *
 * @param {string} src
 * @returns {string} optimized source
 */
export const optimizeCloudinarySecureUrl = (src: string) => {
  const regex = /\/upload\/(?!f_auto|q_auto)/;

  return src.replace(regex, "/upload/f_auto,q_auto/");
};

/**
 *
 * Get Image Src By Sku
 *
 * When unable to use useSkuImage hook, use this function to get the image src
 *
 * @param sku
 * @returns sku image
 */
export const getImageSrcBySku = (sku: string): string =>
  `https://assets.manscaped.com/image/upload/f_auto,q_auto/product-assets/store/skus/${sku}`;

export const getBlogSvgUrl = (url: string, isBlog: boolean) => {
  // Blogs have /blogs prefix in url.
  if (isBlog && process.env.NETLIFY) {
    return url.startsWith("/") ? `/blogs${url}` : `/blogs/${url}`;
  } else {
    return url;
  }
};

/**
 * Both icon sprites (`/icons/...`) and svg sprites (`/svg/...`) are copied to the
 * public root by the same webpack CopyWebpackPlugin config (see gatsby-theme/gatsby-node.js),
 * so neither goes through Gatsby's `pathPrefix`. On Netlify blog builds (`pathPrefix: /blogs`)
 * both need the `/blogs` prefix applied manually to resolve correctly.
 */
const getBlogSpriteHref = (href: string, isBlog: boolean) => {
  if (!isBlog || !process.env.NETLIFY) return href;

  const hashIndex = href.indexOf("#");
  const pathPart = hashIndex === -1 ? href : href.slice(0, hashIndex);
  const fragment = hashIndex === -1 ? "" : href.slice(hashIndex);
  const prefixed = pathPart.startsWith("/") ? `/blogs${pathPart}` : `/blogs/${pathPart}`;

  return `${prefixed}${fragment}`;
};

export const getBlogSvgSpriteHref = (key: SvgSpriteKey, isBlog: boolean) =>
  getBlogSpriteHref(getSvgSpriteHref(key), isBlog);

export const getBlogIconSpriteHref = (key: IconSpriteKey, isBlog: boolean) =>
  getBlogSpriteHref(getIconSpriteHref(key), isBlog);
